'use strict';

// external imports
import React, {
    MouseEvent,
    useCallback,
    useMemo
} from 'react';
import {
    useDispatch,
    useSelector
} from 'react-redux';
import {keys} from 'ts-transformer-keys';

// prime react imports
import {
    Checkbox,
    CheckboxChangeEvent
} from 'primereact/checkbox';
import {confirmPopup} from 'primereact/confirmpopup';
import {Panel} from 'primereact/panel';
import {Toolbar as PrimeToolbar} from 'primereact/toolbar';
import {Tooltip} from 'primereact/tooltip';

// shared imports
import {__} from '~/shared/src/utilities/messageCatalog';
import Item from '~/shared/src/ci/Item';
import {getXmlForContext} from '~/shared/src/utilities/xmlExport';
import {fileDownload} from '~/frontend/src/utilities/fileDownload';
import {LoadDataParameters} from '~/shared/src/utilities/modules/chart';

// frontend imports
import {Mode as ChartDesignerMode} from '~/frontend/src/components/base/ChartDesigner';
import ContentButton from '~/frontend/src/components/base/shared/ContentButton';
import StringField from '~/frontend/src/components/base/shared/field/valueTypeFields/StringField';
import ChartDesignerView from '~/frontend/src/modules/panels/ChartDesignerView';

import {
    getToolbarComponents,
    ToolbarComponents,
    ToolbarLayoutComponents
} from '~/frontend/src/utilities/toolbars';
import {
    getDataRecursive,
    isDataModified,
    isDataModifiedRecursive
} from '~/frontend/src/utilities/dataUtils';

import {
    AppDispatch,
    StoreState
} from '~/frontend/src/store';
import {setMessage} from '~/frontend/src/actions/intercom';
import origSetParam, {SetParamAction} from '~/frontend/src/actions/panel/common/setParam';
import deleteChart from '~/frontend/src/actions/panel/chartDesigner/delete';
import showDeleteSuccessDialog from '~/frontend/src/actions/panelManager/showDeleteSuccessDialog';
import {dataExtraction} from '~/shared/src/utilities/dataExtraction';


export type DefaultProps = {

    /**
     * The mode of the chartDesigner
     */
    mode? : ChartDesignerMode;

    /**
     * Should the save Button shown in the toolbar <strong>or</strong> not.
     */
    showSaveButton? : boolean;

    /**
     * The error count to show as badge on save button.
     *
     * Only applicable if <prop>showSaveButton</prop> is set to true.
     */
    saveButtonErrorCount? : number;

    /**
     * The error tooltip to show on save button.
     *
     * Only applicable if <prop>showSaveButton</prop> is set to true AND <prop>saveButtonErrorCount</prop> is set to an positive integer.
     */
    saveButtonErrorTooltip? : React.ReactNode;

    saveButtonDisabled? : boolean;

    /**
     * Should the clone Button shown in the toolbar <strong>or</strong> not.
     */
    showCloneButton? : boolean;

    /**
     * Should the delete Button shown in the toolbar <strong>or</strong> not.
     */
    showDeleteButton? : boolean;

    deleteButtonDisabled? : boolean;

    /**
     * Show copy XML button.
     */
    showCopyXmlButton? : boolean;

    /**
     * Show download XML button.
     */
    showDownloadXmlButton? : boolean;

    showPanelSelectorDropdown? : boolean;

    /**
     * Show reload button.
     */
    showReloadButton? : boolean;

    /**
     * Show mode selection buttons
     */
    showModeSelector? : boolean;

    showDataCheckbox? : boolean;
    showData? : boolean;
};
export const defaultProps : Required<DefaultProps> = {
    mode                     : 'design',
    saveButtonErrorCount     : 0,
    saveButtonErrorTooltip   : '',
    saveButtonDisabled       : false,
    showCloneButton          : true,
    showCopyXmlButton        : true,
    showData                 : false,
    showDataCheckbox         : true,
    showDeleteButton         : true,
    deleteButtonDisabled     : false,
    showDownloadXmlButton    : true,
    showModeSelector         : true,
    showPanelSelectorDropdown: true,
    showReloadButton         : true,
    showSaveButton           : true
};

export type Props = {

    /**
     * The uniqueId of the panel the component is used in.
     *
     * Mostly used to extract the components state from the global one
     * and to generate unique dom element ids.
     */
    uid : string;

    /**
     * The id of the root item
     */
    rootItemId : number;

    /**
     * The database schema to filter data and metadata for.
     */
    schema : Schema;

    dataContext : string;

    treeViewItem : Item;

    /**
     * Additional parameters for the query.
     *
     * NOTE: A mapping between query fields and contents.
     *
     * USAGE:
     * DataStructure based query:
     *   - key: ${<DataObjectOfQuery.itemKey>.<attributePath>}; value: string
     * Cypher or SQL based query:
     *   - key: ${PlaceholderAsWrittenInQuery}; value: string
     */
    additionalParameters? : Required<LoadDataParameters>[ 'parameters' ];

    /**
     * Event for reload button
     */
    onReloadButtonClick? : () => void;

    onShowDataChange? : ( showData : boolean ) => void;
};

export type ComponentProps = DefaultProps & Props;
export const componentProps = keys<ComponentProps>();

type AllProps = Required<DefaultProps> & Props;

const setParam = origSetParam as <K extends keyof AllProps>( panelKey : string, paramKey : K, paramValue : AllProps[K] ) => SetParamAction;


/**
 * Toolbar
 */
const Toolbar = ( props : AllProps ) => {
    const dispatch : AppDispatch       = useDispatch(),
          {
              additionalParameters,
              dataContext,
              deleteButtonDisabled,
              mode,
              onShowDataChange,
              rootItemId,
              saveButtonDisabled,
              saveButtonErrorCount,
              saveButtonErrorTooltip,
              schema,
              showCloneButton,
              showCopyXmlButton,
              showData,
              showDataCheckbox,
              showDeleteButton,
              showDownloadXmlButton,
              showModeSelector,
              showPanelSelectorDropdown,
              showReloadButton,
              showSaveButton,
              treeViewItem,
              uid
          }                            = props,
          user                         = useSelector( ( state : StoreState ) => state.session?.user ),
          elusiveDataData              = useSelector( ( state : StoreState ) => uid ? state.elusiveData?.[ uid ]?.data : state.elusiveData?.[ uid ]?.metaData ),
          language                     = useMemo(
              () => {
                  return user
                      ? user.language
                      : undefined;
              },
              [
                  user
              ]
          ),
          globalDataVersions           = useMemo(
              () => {
                  return JSON.stringify( elusiveDataData ?? {} );
              },
              [
                  elusiveDataData
              ]
          ),
          data                         = useMemo(
              () => {
                  return getDataRecursive( dataContext, 'CFG', rootItemId );
              },
              [
                  dataContext,
                  globalDataVersions,
                  rootItemId
              ]
          ),
          itemIds                      = useMemo(
              () => {
                  return data.items.map( item => item.id );
              },
              [
                  data
              ]
          ),
          referenceIds                 = useMemo(
              () => {
                  return data.references.map( reference => reference.id );
              },
              [
                  data
              ]
          ),
          settableAdditionalParameters = useMemo(
              () => {
                  const retVal : Required<LoadDataParameters>[ 'parameters' ] = {};

                  // filter out special queryFields for timeSeries if, as they are automatically set to generated values
                  if ( additionalParameters && Object.keys( additionalParameters ).length ) {
                      const item         = rootItemId
                                ? global.data[ dataContext ]?.items[ rootItemId ]
                                : undefined,
                            itemResolved = item
                                ? dataExtraction.extractPropertiesOfItem( item )
                                : undefined;

                      for ( const parameterKey of Object.keys( additionalParameters ) as string[] ) {
                          if (
                              !itemResolved?.timeSeriesIntervalUnit
                              || (
                                  parameterKey !== 'queryTimestamp'
                                  && parameterKey !== 'previousQueryTimestamp'
                                  && parameterKey !== ' nextQueryTimestamp'
                              )
                          ) {
                              retVal[ parameterKey ] = additionalParameters[ parameterKey ];
                          }
                      }
                  }

                  return retVal;
              },
              [
                  additionalParameters,
                  dataContext,
                  rootItemId
              ]
          ),
          additionalParametersPanel    = useMemo(
              () => {
                  if ( settableAdditionalParameters && Object.keys( settableAdditionalParameters ).length ) {
                      const fields : React.ReactNode[] = [];

                      for ( const parameterKey of Object.keys( settableAdditionalParameters ) as string[] ) {
                          fields.push(
                              <StringField
                                  key={`${uid}_key`}
                                  uid={`${uid}_key`}
                                  type="text"

                                  label={parameterKey}
                                  labelAlignment="left"
                                  labelPosition="left"

                                  value={settableAdditionalParameters[ parameterKey ] ?? undefined}

                                  onChange={( value ) => {
                                      dispatch( setParam( uid, 'additionalParameters', {
                                          ...additionalParameters,
                                          [ parameterKey ]: value ?? ''
                                      } ) );
                                  }}

                                  autoComplete={false}
                                  infoTextLayout="hidden"
                                  markDirty={false}
                                  placeholder={'YN.base.frontend.chartDesigner.additionalParameters.placeholder'}

                                  style={{ width: '100%' }}
                              />
                          );
                      }

                      return <ContentButton
                          id={`${uid}_contentButton`}

                          horizontalContentAlign="right"
                          verticalContentAlign="bottom"
                          rounded={false}
                          text={true}
                          icon="pi pi-list"
                          label={__( 'YN.base.frontend.chartDesigner.additionalParameters.button' )}
                          className="yn-option-button yn-view-option-button"
                          contentClassName="yn-contentbutton-overlay-content-page-menu"

                          content={
                              <Panel
                                  className="yn-flex yn-chartdesigner-additionalparameters"
                                  style={{
                                      width: '40rem'
                                  }}
                                  pt={{
                                      content: {
                                          style: {
                                              border: 'none'
                                          }
                                      }
                                  }}
                              >
                                  <div className="fluid formgrid grid" style={{ margin: '0' }}>
                                      <div style={{ width: '100%' }}>
                                          {fields}
                                      </div>
                                  </div>
                              </Panel>
                          }
                      />;
                  }
                  else {
                      return null;
                  }
              },
              [
                  additionalParameters,
                  settableAdditionalParameters
              ]
          ),
          onSaveButtonClick            = useCallback(
              () => {
                  void ChartDesignerView.doSaveData( uid, dispatch );
              },
              [
                  uid
              ]
          ),
          onCloneButtonClick           = useCallback(
              ( event : MouseEvent<HTMLButtonElement> ) => {
                  confirmPopup( {
                      target         : event.currentTarget,
                      message        : 'Not implemnted yet!',
                      className      : 'yn-confirm-popup-notimplemented',
                      icon           : 'pi pi-exclamation-triangle yn-warning',
                      acceptLabel    : __( 'YN.base.common.yes' ),
                      acceptClassName: 'p-button p-button-outlined',
                      rejectLabel    : __( 'YN.base.common.no' ),
                      rejectClassName: 'p-button-secondary p-button-outlined mr-3'
                  } );
              },
              [
                  language
              ]
          ),
          onDeleteButtonClick          = useCallback(
              () => {
                  void dispatch( deleteChart( uid, rootItemId, () => {
                      dispatch( showDeleteSuccessDialog( uid ) );
                  } ) );
              },
              [
                  uid,
                  rootItemId
              ]
          ),
          onCopyXmlApply               = useCallback(
              async () => {
                  await navigator.clipboard.writeText( getXmlForContext( dataContext, itemIds, referenceIds, true, true ) );

                  dispatch( setMessage(
                      uid,
                      'toast',
                      'xmlDataCopiedToClipboard',
                      {
                          severity: 'info',
                          summary : __( 'YN.base.common.xml.dataCopiedToClipboard.title' ),
                          detail  : __( 'YN.base.common.xml.dataCopiedToClipboard.text' )
                      }
                  ) );
              },
              [
                  dataContext,
                  language,
                  itemIds,
                  referenceIds,
                  uid
              ]
          ),
          onDownloadXmlApply           = useCallback(
              () => {
                  fileDownload(
                      getXmlForContext( dataContext, itemIds, referenceIds, true, true ),
                      `ViewDesigner_${treeViewItem.key}`,
                      'xml'
                  );
              },
              [
                  dataContext,
                  language,
                  itemIds,
                  referenceIds,
                  treeViewItem
              ]
          ),
          onReloadButtonClick          = ( event : MouseEvent<HTMLButtonElement> ) => {
              props.onReloadButtonClick?.();
          },
          onModeSelectionButtonClick   = useCallback(
              ( modeToSet : AllProps[ 'mode' ] ) => {
                  dispatch( setParam( uid, 'mode', modeToSet ) );
              },
              [
                  uid
              ]
          ),
          onShowDataCheckboxChange     = useCallback(
              ( event : CheckboxChangeEvent ) => {
                  onShowDataChange?.( event.checked! );
              },
              [
                  onShowDataChange
              ]
          );

    return useMemo(
        () => {
            const layoutComponents : ToolbarLayoutComponents = {
                left : [],
                right: []
            };

            // left
            if ( showSaveButton ) {
                layoutComponents.left.push( {
                    type  : 'saveButton',
                    saveButtonErrorCount,
                    saveButtonErrorTooltip,
                    params: {
                        key: `${uid}_saveButton`,

                        disabled: saveButtonDisabled,
                        onClick : onSaveButtonClick
                    }
                } );
            }

            if ( showCloneButton ) {
                layoutComponents.left.push( {
                    type  : 'cloneButton',
                    params: {
                        key: `${uid}_cloneButton`,

                        onClick: onCloneButtonClick
                    }
                } );
            }

            if ( showDeleteButton ) {
                layoutComponents.left.push( {
                    type  : 'deleteButton',
                    params: {
                        key: `${uid}_deleteButton`,

                        disabled: deleteButtonDisabled,
                        confirm : true,
                        onClick : onDeleteButtonClick
                    }
                } );
            }

            layoutComponents.left.push( {
                type: 'separator'
            } );

            if ( showCopyXmlButton ) {
                layoutComponents.left.push( {
                    type  : 'copyXml',
                    params: {
                        key: `${uid}_copyXmlButton`,

                        uid,

                        onExpand: () => {
                            void onCopyXmlApply();
                            return false;
                        },
                        onApply : () => {
                            void onCopyXmlApply();
                        }
                    }
                } );
            }

            if ( showDownloadXmlButton ) {
                layoutComponents.left.push( {
                    type  : 'downloadXml',
                    params: {
                        key: `${uid}_downloadXmlButton`,

                        uid,

                        onExpand: () => {
                            onDownloadXmlApply();
                            return false;
                        },
                        onApply : onDownloadXmlApply
                    }
                } );
            }

            layoutComponents.left.push( {
                type: 'separator'
            } );

            if ( showModeSelector ) {
                layoutComponents.left.push( {
                    type  : 'buttonSet',
                    params: {
                        key: `${uid}_modeSelectionButtons`,

                        buttons: [
                            {
                                type  : 'button',
                                params: {
                                    key: `${uid}_designModeButton`,

                                    label    : __( 'YN.base.frontend.chartDesigner.mode.design.label' ),
                                    onClick  : onModeSelectionButtonClick.bind( null, 'design' ),
                                    className: mode === 'design' ? '' : 'p-button-outlined'
                                }
                            },
                            {
                                type  : 'button',
                                params: {
                                    key: `${uid}_inputPanelButton`,

                                    label    : __( 'YN.base.frontend.chartDesigner.mode.input.label' ),
                                    onClick  : onModeSelectionButtonClick.bind( null, 'input' ),
                                    className: mode === 'input' ? '' : 'p-button-outlined'
                                }
                            }
                        ]
                    }
                } );
            }

            layoutComponents.left.push( {
                type: 'separator'
            } );

            if ( showDataCheckbox ) {
                if ( additionalParametersPanel ) {
                    const hasEmptyParameter = Boolean( additionalParameters && Object.values( settableAdditionalParameters ).includes( '' ) );

                    layoutComponents.left.push( {
                        type: 'genericNode',

                        params: <div
                            key={`${uid}_showDataContainer`}
                            id={`${uid}_showDataContainer`}
                            className={`yn-toolbar-container ${hasEmptyParameter ? 'yn-toolbar-container-error' : ''}`}
                        >
                            {additionalParametersPanel}
                            <div key={`${uid}_showDataCheckbox`} className="flex align-items-center field ml-2">
                                <label
                                    htmlFor={`${uid}_showDataCheckbox`}
                                    className="block"
                                >
                                    {__( 'YN.base.frontend.chartDesigner.showData.label' )}
                                </label>
                                <Checkbox
                                    id={`${uid}_showDataCheckbox`}
                                    checked={!hasEmptyParameter && showData}
                                    disabled={hasEmptyParameter}
                                    onChange={onShowDataCheckboxChange}
                                    style={{ flex: '0 0 auto', width: 'auto' }}
                                />
                            </div>
                            {hasEmptyParameter &&
                                <Tooltip
                                    key={`${uid}_showDataContainerTooltip`}
                                    target={`#${uid}_showDataContainer`}
                                    my="left top"
                                    at="left bottom"
                                    className="yn-tooltip"
                                    showDelay={500}
                                >
                                    {__( 'YN.base.frontend.chartDesigner.additionalParameters.error.tooltip' )}
                                </Tooltip>}
                        </div>
                    } );
                }
                else {
                    layoutComponents.left.push( {
                        type: 'genericNode',

                        params: <div
                            key={`${uid}_showDataCheckbox`}
                            className="flex align-items-center field"
                        >
                            <label
                                htmlFor={`${uid}_showDataCheckbox`}
                                className="block"
                            >
                                {__( 'YN.base.frontend.chartDesigner.showData.label' )}
                            </label>
                            <Checkbox
                                id={`${uid}_showDataCheckbox`}
                                checked={showData}
                                onChange={onShowDataCheckboxChange}
                                style={{ flex: '0 0 auto', width: 'auto' }}
                            />
                        </div>
                    } );
                }
            }

            // right
            if ( showPanelSelectorDropdown ) {
                const item       = rootItemId
                          ? global.data[ dataContext ]?.items[ rootItemId ]
                          : undefined,
                      itemParams = item
                          ? {
                              schema  : item.schema,
                              itemId  : item.id,
                              itemType: item.itemTypeId
                          }
                          : {};

                layoutComponents.right.push( {
                    type  : 'panelSelectorDropdown',
                    params: {
                        key: `${uid}_panelSelector`,

                        uid        : `${uid}_panelSelector`,
                        labeled    : true,
                        style      : { width: '18rem' },
                        currentView: `ChartDesignerView`,
                        ...itemParams
                    }
                } );
            }

            layoutComponents.right.push( {
                type: 'separator'
            } );

            if ( showReloadButton ) {
                layoutComponents.right.push( {
                    type  : 'reloadButton',
                    params: {
                        key: `${uid}_reloadButton`,

                        confirm: () => rootItemId ? isDataModifiedRecursive( dataContext, schema, rootItemId ) : isDataModified( dataContext, schema! ),
                        onClick: onReloadButtonClick
                    }
                } );
            }

            const toolbarComponents : ToolbarComponents = getToolbarComponents( layoutComponents );

            return <PrimeToolbar
                start={toolbarComponents.left}
                end={toolbarComponents.right}
                className="yn-panel-toolbar shadow-4"
                style={{ zIndex: 5 }}
            />;
        },
        [
            additionalParameters,
            additionalParametersPanel,
            dataContext,
            language,
            mode,
            onCloneButtonClick,
            onCopyXmlApply,
            onDeleteButtonClick,
            onDownloadXmlApply,
            onModeSelectionButtonClick,
            onSaveButtonClick,
            onShowDataCheckboxChange,
            settableAdditionalParameters,
            rootItemId,
            saveButtonErrorCount,
            saveButtonErrorTooltip,
            showCloneButton,
            showCopyXmlButton,
            showData,
            showDeleteButton,
            showDownloadXmlButton,
            showModeSelector,
            showReloadButton,
            showSaveButton,
            uid
        ]
    );
};
Toolbar.defaultProps = defaultProps;
Toolbar.displayName = 'ChartDesigner-Toolbar';

export default Toolbar;
